/** Tiny DOM helpers. No innerHTML anywhere the user's text can reach: the
 *  description is echoed back on screen, so escaping is not optional. */

type Attributes = Record<string, string | number | boolean | null | undefined>
export type Child = Node | string | number | null | undefined | false

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attributes: Attributes = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [key, value] of Object.entries(attributes)) {
    if (value === null || value === undefined || value === false) continue
    if (key === 'class') node.className = String(value)
    else if (value === true) node.setAttribute(key, '')
    else node.setAttribute(key, String(value))
  }
  append(node, children)
  return node
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue
    parent.appendChild(
      typeof child === 'string' || typeof child === 'number'
        ? document.createTextNode(String(child))
        : child,
    )
  }
}

export function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild)
}

/** A titled card, the only container this interface uses. */
export function card(title: string, note: string | null, ...children: Child[]): HTMLElement {
  return el('section', { class: 'card' },
    el('h2', {}, title),
    note ? el('p', { class: 'note' }, note) : null,
    ...children)
}

export function table(
  headers: { label: string; num?: boolean }[],
  rows: { cells: Child[]; className?: string | undefined }[],
  className?: string,
): HTMLElement {
  return el('div', { class: 'scroll' },
    el('table', { class: className ?? null },
      el('thead', {}, el('tr', {}, ...headers.map((header) =>
        el('th', { class: header.num ? 'num' : null }, header.label)))),
      el('tbody', {}, ...rows.map((row) =>
        el('tr', { class: row.className ?? null },
          ...row.cells.map((cell, index) =>
            el('td', {
              class: [
                headers[index]?.num ? 'num' : null,
                index === 0 && headers.length > 4 ? null : null,
              ].filter(Boolean).join(' ') || null,
            }, cell)))))))
}

/**
 * A row of short labelled notes.
 *
 * `.note` is capped at a reading measure, which is right for a paragraph and
 * wrong under a full-width table: the prose renders as a narrow ribbon with half
 * the card empty beside it. Splitting the same text into labelled columns fills
 * the width AND keeps every line readable — and it turns a wall of small type
 * into something scannable, which is what a footnote is for.
 */
export function notesStrip(
  entries: { label: string; body: Child[] }[],
  className?: string,
): HTMLElement {
  return el('dl', { class: ['notes-strip', className].filter(Boolean).join(' ') },
    ...entries.map((entry) => el('div', {},
      el('dt', {}, entry.label),
      el('dd', {}, ...entry.body))))
}

/**
 * A <details> whose open state survives a re-render.
 *
 * Every control in this interface redraws the whole result, so a disclosure that
 * carried its state in the DOM closed itself the moment anyone touched a control
 * inside it — you could not move a slider twice without reopening the panel it
 * lives in. The state belongs to the session, not to a node that gets thrown
 * away, so it is kept here and read back on each build.
 */
const OPEN_DISCLOSURES = new Set<string>()

export function disclosure(
  id: string,
  summary: string,
  options: { openByDefault?: boolean; className?: string } = {},
  ...children: Child[]
): HTMLElement {
  if (options.openByDefault && !OPEN_DISCLOSURES.has(`${id}:seen`)) {
    OPEN_DISCLOSURES.add(`${id}:seen`)
    OPEN_DISCLOSURES.add(id)
  }
  const node = el('details', {
    class: options.className ?? 'refine',
    open: OPEN_DISCLOSURES.has(id),
  }, el('summary', {}, summary), ...children)
  node.addEventListener('toggle', () => {
    if (node.open) OPEN_DISCLOSURES.add(id)
    else OPEN_DISCLOSURES.delete(id)
  })
  return node
}
